import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Building2, Users, BarChart3, Package, Shield, Settings2, FileText,
  GraduationCap, ShieldCheck, LogOut, Menu, X, Sun, Moon, ChevronLeft, Home, Mail, Target, ClipboardList, Archive
} from 'lucide-react';
import { isPlatformOwner } from '@/lib/platformOwner';
import { BACKOFFICE_NAV, tieneSaPerm } from '@/lib/backofficeNav';

import { logoForTheme, isotipoForTheme, APP_NAME } from '@/lib/brand';

const ICONS = {
  '/backoffice': Building2,
  '/backoffice/uso-ia': BarChart3,
  '/backoffice/planes': Package,
  '/backoffice/propiedades': Home,
  '/backoffice/reportes': BarChart3,
  '/backoffice/superadmins': Shield,
  '/backoffice/user-tenants': ShieldCheck,
  '/backoffice/tipos-usuario': ShieldCheck,
  '/backoffice/integraciones': Settings2,
  '/backoffice/email-config': Mail,
  '/backoffice/tracking-publicitario': Target,
  '/backoffice/proyectos': ClipboardList,
  '/backoffice/academy': GraduationCap,
  '/backoffice/bitacora': FileText,
  '/backoffice/suspensiones': Archive,
};

export default function BackofficeSidebar() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isOwner = isPlatformOwner(user);
  const visibleNav = BACKOFFICE_NAV.filter(item => tieneSaPerm(user, item.perm));

  const isActive = (path) => location.pathname === path || (path !== '/backoffice' && location.pathname.startsWith(path));

  const NavItem = ({ item }) => {
    const Icon = ICONS[item.path] || Building2;
    return (
    <button
      onClick={() => { navigate(item.path); setMobileOpen(false); }}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all group ${
        isActive(item.path)
          ? 'bg-[#A5D62C]/10 text-[#A5D62C] font-medium'
          : 'text-white/40 hover:text-white/70 hover:bg-white/[0.03]'
      }`}
      data-testid={`bo-nav-${item.path.replaceAll('/', '-').replace(/^-+/, '')}`}
    >
      <Icon className={`w-4.5 h-4.5 shrink-0 ${isActive(item.path) ? 'text-[#A5D62C]' : 'text-white/30 group-hover:text-white/50'}`} />
      {!collapsed && <span className="truncate">{item.label === 'Academy' ? `${APP_NAME} Academy` : item.label}</span>}
    </button>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-4 pt-5 pb-4">
        <div className="flex items-center gap-2 min-h-[40px]" data-testid="backoffice-brand-wrap">
          {collapsed ? (
            <img src={isotipoForTheme(theme)} alt="LEADCEM isotipo" className="h-9 w-9 object-contain" data-testid="backoffice-isotipo-logo" />
          ) : (
            <div className="space-y-1">
              <img src={logoForTheme(theme)} alt="LEADCEM logo" className="h-11 w-auto object-contain" data-testid="backoffice-horizontal-logo" />
              <p className="text-[8px] uppercase tracking-[3px] text-muted-foreground">Integraciones</p>
            </div>
          )}
        </div>
        {!collapsed && isOwner && (
          <div className="mt-2 px-1">
            <p className="text-[9px] text-amber-400/60 flex items-center gap-1">
              <Shield className="w-2.5 h-2.5" /> Owner
            </p>
          </div>
        )}
      </div>

      {/* Nav Items */}
      <div className="flex-1 overflow-y-auto px-3 space-y-0.5">
        {visibleNav.map(item => <NavItem key={item.path} item={item} />)}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-white/5 space-y-2">
        <div className="flex items-center gap-2 px-2">
          <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-white/60 transition-colors" data-testid="bo-theme-toggle">
            {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>
          {!collapsed && (
            <button onClick={() => setCollapsed(true)} className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-white/60 transition-colors">
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-red-400/60 hover:text-red-400 hover:bg-red-500/5 text-xs transition-colors" data-testid="bo-logout">
          <LogOut className="w-3.5 h-3.5" /> {!collapsed && 'Cerrar sesion'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile trigger */}
      <button onClick={() => setMobileOpen(true)} className="fixed top-3 left-3 z-50 p-2 rounded-lg bg-black/80 border border-white/10 md:hidden" data-testid="bo-mobile-menu">
        <Menu className="w-5 h-5 text-white" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#0a0c12] border-r border-white/5">
            <button onClick={() => setMobileOpen(false)} className="absolute top-3 right-3 p-1 text-white/40 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className={`hidden md:flex flex-col fixed left-0 top-0 bottom-0 bg-[#0a0c12] border-r border-white/5 z-40 transition-all duration-200 ${collapsed ? 'w-16' : 'w-56'}`}>
        {collapsed && (
          <button onClick={() => setCollapsed(false)} className="absolute -right-3 top-6 w-6 h-6 rounded-full bg-[#0a0c12] border border-white/10 flex items-center justify-center text-white/40 hover:text-white z-50">
            <Menu className="w-3 h-3" />
          </button>
        )}
        {sidebarContent}
      </div>
    </>
  );
}
